// Client side of the Bible-pipeline reliability telemetry
// (2026-08-25 Bible reliability arc; server: api/telemetry.ts).
//
// Fire-and-forget, failure-silent, and PRIVACY-SAFE BY CONSTRUCTION:
// nothing in this module ever transmits prayer text, user identifiers,
// or free text a user typed. Callers pass error codes, book slugs,
// HTTP statuses, and boolean flags only; strings are length-capped
// before sending, and the server re-sanitizes every field
// against its own allowlist.
//
// Zero internal imports (probe-safe leaf: bsb-bootstrap imports this,
// and probes import bsb-bootstrap under tsx/node where import.meta.env
// and navigator may be absent — every browser global is guarded).
//
// Session bounds: identical events (same event + code + slug) send
// once per page session, and the session sends at most
// MAX_EVENTS_PER_SESSION total, so a retry loop can never turn into a
// telemetry flood.

export type BibleTelemetryEvent =
  | 'bible_chapter_load_failed'
  | 'bible_hydration_failed'
  | 'bible_hydration_repair'
  | 'bible_asset_masquerade';

const ENDPOINT = '/api/telemetry';
const MAX_EVENTS_PER_SESSION = 20;
const MAX_FIELD_LENGTH = 200;

const sentKeys = new Set<string>();
let sentCount = 0;

export function reportBibleEvent(
  event: BibleTelemetryEvent,
  fields: Record<string, string | number | boolean | null | undefined> = {},
): void {
  try {
    if (typeof window === 'undefined') return;
    if (sentCount >= MAX_EVENTS_PER_SESSION) return;

    const clean: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value === null || value === undefined) continue;
      clean[key] =
        typeof value === 'string' ? value.slice(0, MAX_FIELD_LENGTH) : value;
    }
    if (typeof navigator !== 'undefined') {
      clean.online = navigator.onLine;
    }

    const dedupeKey = `${event}:${String(clean.code ?? '')}:${String(clean.slug ?? clean.book ?? '')}`;
    if (sentKeys.has(dedupeKey)) return;
    sentKeys.add(dedupeKey);
    sentCount += 1;

    const payload = JSON.stringify({ v: 1, events: [{ e: event, f: clean }] });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, payload);
      return;
    }
    if (typeof fetch !== 'undefined') {
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,
        body: payload,
      }).catch(() => {});
    }
  } catch {
    // Telemetry must never be able to break the feature it observes.
  }
}

// Extracts the bounded, transmit-safe facts from a Bible-pipeline
// error (duck-typed so this module stays a zero-import leaf; works on
// BibleHydrationError and anything Error-shaped).
export function bibleErrorFields(
  err: unknown,
): Record<string, string | number | undefined> {
  const out: Record<string, string | number | undefined> = {};
  if (!err || typeof err !== 'object') return out;
  const anyErr = err as {
    code?: unknown;
    message?: unknown;
    context?: unknown;
  };
  if (typeof anyErr.code === 'string') out.code = anyErr.code;
  const ctx = anyErr.context;
  if (ctx && typeof ctx === 'object') {
    const c = ctx as Record<string, unknown>;
    for (const key of ['slug', 'status', 'contentType', 'op']) {
      const value = c[key];
      if (typeof value === 'string' || typeof value === 'number') out[key] = value;
    }
  }
  return out;
}
